/* Warehouse Bold — reviews & questions ("The Logbook").
 *
 * Judge.me's widget markup is not the design's, so their widget is treated
 * as DATA + FUNCTIONS only: reviews and questions are read out of it and
 * re-rendered in the design's own record language, while their bundle keeps
 * supplying the working machinery — the write-review modal and the
 * question-submission form. The widget itself lives hidden inside
 * [data-wb-jdgm]; when ASK A QUESTION reveals that panel, CSS trims it to
 * only the question form.
 *
 * ⚠ The `judgeme.widget` metafield carries only Judge.me's FIRST page of
 * reviews (5 by default). The metafield is the seed, and everything beyond
 * it comes from Judge.me's widget endpoint. Eagerly, not behind a button:
 * the histogram doubles as the filter control and its counts are
 * product-wide, so filtering a partial list would show "3" on the bar and
 * list one review.
 *
 * View pipeline (one direction, always reconciled): sort → filter → display
 * cap. Sort reorders the data, filters hide rows via [hidden] (every row
 * stays in the DOM for crawlers), and the cap shows five at a time behind
 * "Show N more reviews".
 */
(() => {
  const API = 'https://api.judge.me/reviews/reviews_for_widget';
  const PER = 50;
  const PAGE = 5;

  const init = () => {
    const panel = document.querySelector('[data-wb-jdgm]');
    const out = document.querySelector('[data-wb-records]');

    const I18N = (() => {
      const el = document.querySelector('[data-wb-rv-i18n]');
      try { return el ? JSON.parse(el.textContent) : {}; } catch (e) { return {}; }
    })();
    const plural = (n, one, other) => (n === 1 ? (one || '') : String(other || '').replace('987654', n));

    const smooth = () => (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth');
    const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const txt = (el, sel) => { const n = el.querySelector(sel); return n ? n.textContent.trim() : ''; };

    /* Bodies must read the same whether they came from the metafield or the
       API, and Judge.me wraps multi-paragraph copy in <p> blocks (answers
       especially). Paragraphs join with a blank line; pre-line CSS renders
       it. innerText is unavailable (the sources are display:none), so <br>
       unwraps by hand. */
    const bodyText = (el) => {
      if (!el) return '';
      const clone = el.cloneNode(true);
      clone.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
      const ps = [...clone.querySelectorAll('p')];
      const s = ps.length
        ? ps.map((par) => par.textContent).join('\n\n')
        : clone.textContent;
      return s.replace(/ /g, ' ').trim();
    };

    const fmtDate = (iso) => {
      if (!iso) return '';
      const d = new Date(iso + 'T00:00:00Z');
      if (isNaN(d)) return '';
      const lang = document.documentElement.lang || 'en';
      const locale = lang === 'en' ? 'en-AU' : lang;
      return d.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
    };
    const starRow = (n) => {
      // The glyphs are decoration; the visually-hidden sentence is the
      // rating a screen reader announces (mirrors wb-stars.liquid).
      let h = '<span class="wb-sr">' + esc(String(I18N.rated || '').replace('@@R@@', n)) + '</span>';
      h += '<span class="wb-stars" aria-hidden="true">';
      for (let i = 1; i <= 5; i += 1) h += '<span class="wb-star' + (i <= n ? ' is-on' : '') + '">&#9733;</span>';
      return h + '</span>';
    };

    /* ---- Judge.me's own functions, relayed from our buttons ----
       Wired even on zero-review products (the invite state) and independent
       of our custom records.

       ⚠ Their bundle binds ONLY the controls inside its own widget — a
       page-level element carrying their classes is ignored (and their CSS
       reskins anything carrying .jdgm-write-rev-link, which is why our
       buttons carry data-wb-* hooks instead). */
    const widgetMounted = () => !!(panel && panel.querySelector('.jdgm-subtab__name, .jdgm-ask-question-btn, .jdgm-rev-widg__header'));
    const loaderAlive = () => !!(window.jdgm && window.jdgm._loaderExecuted);

    if (panel) {
      // WRITE A REVIEW → their modal (modal_write_review_flow is on).
      // ⚠ Retried: the bundle mounts its DOM well before it binds the
      // write-review link (observed: dead at ~4.5s, bound moments later).
      const modalOpen = () => {
        const m = document.querySelector('.jdgm-write-review-modal');
        return !!(m && m.offsetHeight > 0);
      };
      /* Backdrop-click closes their review modal, for parity with the ask
         drawer. Delegated at the document because their modal is built on
         demand, and gated on the exact element so a click anywhere inside
         the card can never close it: their flex container spans the
         viewport, so the dimmed area beside the drawer IS that container. */
      document.addEventListener('click', (e) => {
        const t = e.target;
        if (!t || !t.classList) return;
        if (!t.classList.contains('jdgm-write-review-modal__container')) return;
        const x = document.querySelector('.jdgm-write-review-modal__close-btn');
        if (x) x.click();
      });

      document.querySelectorAll('[data-wb-write]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault(); // href="#" would jump to the top of the page
          const attempt = (n) => {
            if (modalOpen()) return;
            const theirs = panel.querySelector('.jdgm-write-rev-link');
            if (theirs) theirs.click();
            // 12 × 500ms comfortably outlasts the slowest observed binding
            // (~4.5s). No reveal fallback: the revealed panel is CSS-trimmed
            // to the QUESTION form, so exposing it here would scroll the
            // visitor to the wrong UI (or an empty strip).
            if (n < 12) setTimeout(() => attempt(n + 1), 500);
          };
          attempt(1);
        });
      });
    }

    /* =================== The reviews view =================== */

    const parse = (root) => [...root.querySelectorAll('.jdgm-rev')].map((r) => {
      const stars = r.querySelector('.jdgm-rev__rating');
      const stamp = r.querySelector('.jdgm-rev__timestamp');
      return {
        id: r.getAttribute('data-review-id') || '',
        rating: parseInt(stars ? stars.getAttribute('data-score') : '0', 10) || 0,
        // Judge.me renders relative dates client-side; the absolute
        // timestamp is on the element, so use that.
        date: stamp ? (stamp.getAttribute('data-content') || '').split(' ')[0] : '',
        author: txt(r, '.jdgm-rev__author') || 'Anonymous',
        verified: r.getAttribute('data-verified-buyer') === 'true',
        title: txt(r, '.jdgm-rev__title'),
        // Bodies render character-exact: a "rivot" typo, a missing space
        // after a comma, a mid-sentence capital all stay as written.
        body: bodyText(r.querySelector('.jdgm-rev__body')),
        pics: [...r.querySelectorAll('.jdgm-rev__pics img')]
          .map((i) => i.getAttribute('data-src') || i.getAttribute('src') || '')
          .filter(Boolean)
      };
    });

    let reviews = panel ? parse(panel) : [];

    if (out && reviews.length) {
      const productId = out.dataset.productId;
      const shop = out.dataset.shop;
      let total = parseInt(out.dataset.total, 10) || reviews.length;
      let page = 1;

      // View state. Sort reorders data; filters and the cap only hide.
      let sortMode = 'recent';
      let starFilter = null;
      let onlyVerified = false;
      let onlyPhotos = false;
      let shown = PAGE;
      let viewVersion = 0;
      let rows = [];      // DOM rows, parallel to `sorted`
      let sorted = [];

      const VERIFIED = (out.dataset.verifiedLabel || 'Verified');
      const clear = document.querySelector('[data-wb-clear]');
      const clearAllBtn = document.querySelector('[data-wb-clear-all]');
      const more = document.querySelector('[data-wb-more]');
      const emptyBox = document.querySelector('[data-wb-empty]');
      const announce = document.querySelector('[data-wb-announce]');
      const vToggle = document.querySelector('[data-wb-toggle="verified"]');
      const pToggle = document.querySelector('[data-wb-toggle="photos"]');
      const sortSel = document.querySelector('[data-wb-sort]');
      const tabrow = document.querySelector('[data-wb-tabrow]');
      const bars = [...document.querySelectorAll('[data-wb-filter]')];

      const sortReviews = () => {
        // Authored order IS newest-first (Judge.me's default), so "recent"
        // keeps it and rating sorts tie-break back to it.
        const next = reviews.slice();
        if (sortMode === 'high' || sortMode === 'low') {
          const dir = sortMode === 'high' ? -1 : 1;
          next.sort((a, b) => {
            const d = dir * (a.rating - b.rating);
            if (d) return d;
            return (b.date || '').localeCompare(a.date || '');
          });
        }
        return next;
      };

      const rowHTML = (r) => (
        '<article class="wb-rec-row" data-score="' + r.rating + '">' +
          '<div class="wb-rec-row__who">' +
            '<div class="wb-rec-row__name">' + esc(r.author) + '</div>' +
            '<div class="wb-rec-row__date">' + esc(fmtDate(r.date)) + '</div>' +
            // Verified gets a chip; unverified simply omits it. There is
            // never an "unverified" label — it reads as an accusation.
            (r.verified ? '<span class="wb-rec-row__chip">&#10003; ' + esc(VERIFIED) + '</span>' : '') +
          '</div>' +
          '<div class="wb-rec-row__body">' +
            starRow(r.rating) +
            (r.title ? '<h3 class="wb-rec-row__title">' + esc(r.title) + '</h3>' : '') +
            '<p class="wb-rec-row__text">' + esc(r.body) + '</p>' +
            /* Thumbnails inside a review open the SAME shared gallery, at this
               photo. Resolved by URL at click time rather than by a baked-in
               index, because this markup is re-rendered on every sort/filter
               while the photo list is not. */
            (r.pics.length ? '<div class="wb-rec-row__pics">' + r.pics.map((u) =>
              '<button type="button" class="wb-rec-row__pic" data-wb-lb-url="' + esc(u) + '"' +
              (LB_ID ? ' data-wb-drawer="' + esc(LB_ID) + '" aria-haspopup="dialog"' : '') + '>' +
              '<img src="' + esc(u) + '" alt="" loading="lazy"></button>').join('') + '</div>' : '') +
          '</div>' +
        '</article>'
      );

      const renderAll = () => {
        sorted = sortReviews();
        out.innerHTML = sorted.map(rowHTML).join('');
        rows = [...out.children];
      };

      const matches = (r) => {
        if (starFilter !== null && r.rating !== starFilter) return false;
        if (onlyVerified && !r.verified) return false;
        if (onlyPhotos && !r.pics.length) return false;
        return true;
      };
      const anyFilter = () => starFilter !== null || onlyVerified || onlyPhotos;

      const applyView = () => {
        const setIdx = [];
        sorted.forEach((r, i) => { if (matches(r)) setIdx.push(i); });
        rows.forEach((el) => { el.hidden = true; });
        setIdx.slice(0, shown).forEach((i) => { rows[i].hidden = false; });

        /* The rest-count is honest about reviews not yet fetched: with no
           filter active the true universe is `total`, which the server knows
           even while memory holds only the seed. Filtered universes can only
           be counted from memory. */
        const universe = anyFilter() ? setIdx.length : Math.max(total, setIdx.length);
        const rest = universe - Math.min(shown, setIdx.length);
        if (more) {
          if (rest > 0) {
            more.hidden = false;
            more.textContent = plural(rest, I18N.more_one, I18N.more_other);
          } else {
            more.hidden = true; // removed, never disabled
          }
        }
        if (emptyBox) emptyBox.hidden = setIdx.length !== 0;
        if (clear) clear.hidden = !anyFilter();
        if (announce) {
          announce.textContent = setIdx.length === 0
            ? (I18N.no_match || '')
            : plural(setIdx.length, I18N.showing_one, I18N.showing_other);
        }
        syncControls();
      };

      const syncControls = () => {
        bars.forEach((b) => {
          const on = parseInt(b.dataset.wbFilter, 10) === starFilter;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        if (vToggle) vToggle.setAttribute('aria-pressed', onlyVerified ? 'true' : 'false');
        if (pToggle) pToggle.setAttribute('aria-pressed', onlyPhotos ? 'true' : 'false');
      };

      /* Toggle pills surface only when their filter can match something,
         with counts from the full set — both wait for the fetch on
         multi-page products. */
      const paintToggles = () => {
        if (reviews.length < total) return;
        const v = reviews.filter((r) => r.verified).length;
        const ph = reviews.filter((r) => r.pics.length).length;
        if (vToggle) { vToggle.querySelector('.n').textContent = v; vToggle.hidden = v === 0; }
        if (pToggle) { pToggle.querySelector('.n').textContent = ph; pToggle.hidden = ph === 0; }
      };

      /* ---------------- Customer photo viewer ----------------
         Clicking a photo used to do nothing at all — the tiles were bare <img>.
         This restores what Judge.me's own lightbox did (photo beside its review)
         in the WB language.
         ⚠ ONE shared photo list backs both surfaces, so the arrows walk every
         customer photo on the page no matter which thumbnail was clicked
         (Owen's choice, 2026-08-16).
         ⚠ State lives here, NOT in the DOM: the review list is replaced wholesale
         on every sort, filter and page load, so anything stored in those nodes
         would be destroyed under the open dialog. */
      /* ⚠ Composed from LOCALE KEYS, never from strings in this file. This theme
         publishes de/es/fr/ko, and a label built in JavaScript stays English in
         all four with no seam visible from the English storefront — the same
         trap that left "Ships from Australia" in the German buy box. */
      /* ⚠ Keep the photo_* locale strings free of ' " and & in EVERY locale.
         They come through Liquid's `| t`, which HTML-escapes, and the tile
         label is then esc()'d again on its way into innerHTML — so an
         apostrophe arrives as "William&#39;s" and is read out as an entity.
         French lost "de l'avis" to this; the author name itself is escaped
         separately by that esc() and may contain anything. */
      const photoWho = (ph) => (ph.review && ph.review.author ? ph.review.author : '');
      const fillPhoto = (tmpl, i, n, who) => String(tmpl || '')
        .replace('@@N@@', i + 1).replace('@@M@@', n).replace('@@WHO@@', who || '');
      // The button's name says what activating it DOES, and where in the set it sits.
      const photoLabel = (ph, i, n) => {
        const who = photoWho(ph);
        return fillPhoto(who ? I18N.photo_open : I18N.photo_open_anon, i, n, who);
      };
      // alt describes the PICTURE. Position is UI state and belongs to the
      // counter, or every arrow press leaves a stale "3 of 8" in the alt text.
      const photoAlt = (ph) => {
        const who = photoWho(ph);
        return who ? fillPhoto(I18N.photo_alt, 0, 0, who) : (I18N.photo_alt_anon || '');
      };

      /* ⚠ Judge.me serves the SAME file from two hosts — review-images.judge.me
         and review-images.judgeme.com — and hands us one or the other depending
         on whether a photo came from the rendered review or the gallery feed.
         Keying on origin+path therefore counted every photo twice: two photos
         showed as "1 / 4". The path alone identifies the file (Judge.me stamps
         each upload with a unique id), so that is the key. */
      const photoKey = (u) => {
        try { return new URL(u, location.href).pathname; }
        catch (e) { return String(u).split('?')[0]; }
      };

      /* ⚠ The deck thumbnails are 160px wide. Handed straight to a viewer that
         paints them ~700px they were a blurry upscale, so the viewer asks the
         CDN for a full-size copy (verified: width=1600 returns 1416x1598).
         Only judge.me's own image hosts take these params — anything else is
         passed through untouched rather than guessed at. */
      const hiRes = (u) => {
        try {
          const url = new URL(u, location.href);
          if (!/(^|\.)judge\.?me\.com$|(^|\.)judge\.me$/.test(url.hostname)) return u;
          url.search = '';
          url.searchParams.set('quality', '85');
          url.searchParams.set('width', '1600');
          return url.toString();
        } catch (e) { return u; }
      };

      /* ⚠ Read the dialog's id at RUNTIME. This file is a static asset, not
         Liquid, so it cannot emit the section-suffixed id into the tile markup
         the way the dialog itself does. If the dialog is missing, or the engine
         has no showModal (in which case wb-drawer.js's open() bails too), LB_ID
         stays empty and the tiles render without a trigger attribute — inert,
         which is what they were before this feature and better than a control
         that opens nothing. */
      const lbEl = document.querySelector('[data-wb-lb]');
      const LB_OK = !!(lbEl && lbEl.id && typeof lbEl.showModal === 'function');
      const LB_ID = LB_OK ? lbEl.id : '';

      const lb = (() => {
        const el = lbEl;
        if (!LB_OK) return { setPhotos() {}, show() { return false; } };
        const img = el.querySelector('[data-wb-lb-img]');
        const fig = el.querySelector('.wb-lb__fig');
        const frame = el.querySelector('[data-wb-lb-frame]');
        const phEl = el.querySelector('[data-wb-lb-ph]');
        const side = el.querySelector('[data-wb-lb-side]');
        const count = el.querySelector('[data-wb-lb-count]');
        const prev = el.querySelector('[data-wb-lb-prev]');
        const next = el.querySelector('[data-wb-lb-next]');
        let list = [];
        let i = 0;
        let opener = null;

        const paint = () => {
          const ph = list[i];
          if (!ph) return;
          // Cached thumbnail behind, full-size in front: the frame fills at once
          // and sharpens, instead of sitting empty for the length of a download.
          // ⚠ encodeURI + quote-strip: this is a third-party URL going inside a
          // CSS url(), where a bare " would break out of the declaration.
          const cssUrl = 'url("' + encodeURI(ph.url).replace(/"/g, '%22') + '")';
          phEl.style.backgroundImage = cssUrl;
          // The blurred Marketplace-style backdrop (frame's ::before). The
          // thumbnail is plenty: behind 26px of blur its resolution is
          // irrelevant, and it is already cached from the deck.
          frame.style.setProperty('--wb-lb-bg', cssUrl);
          /* ⚠ Hide the img until it has actually decoded. It is sized to fill
             the frame, and Chromium paints the alt text and a broken-image icon
             inside a sized img for as long as its source is in flight — so the
             shopper saw "Customer photo from William (1 of 2)" scrawled across
             the placeholder on every open. Revealed by the load handler below. */
          img.classList.remove('is-loaded');
          img.src = hiRes(ph.url);
          img.alt = photoAlt(ph);
          // A cache hit can be complete the moment src is assigned, in which
          // case the load event may already have come and gone. Never leave the
          // photo hidden waiting for an event that will not arrive.
          if (img.complete && img.naturalWidth) img.classList.add('is-loaded');
          count.textContent = (i + 1) + ' / ' + list.length;
          const r = ph.review;
          // Gallery-only photos get NO panel rather than an empty one.
          el.classList.toggle('is-solo', !r);
          side.innerHTML = !r ? '' : (
            '<div class="wb-lb__who">' + esc(r.author) +
              (r.verified ? '<span class="wb-lb__chip">&#10003; ' + esc(out.dataset.verifiedLabel || 'Verified') + '</span>' : '') +
            '</div>' +
            // ⚠ fmtDate, matching rowHTML(). Printing the raw ISO here put
            // "2025-05-30" in the viewer beside "30 May 2025" in the list.
            (r.date ? '<div class="wb-lb__date">' + esc(fmtDate(r.date)) + '</div>' : '') +
            starRow(r.rating) +
            (r.title ? '<h3 class="wb-lb__title">' + esc(r.title) + '</h3>' : '') +
            '<p class="wb-lb__text">' + esc(r.body) + '</p>'
          );
          // One photo means no arrows to press.
          const many = list.length > 1;
          prev.hidden = !many;
          next.hidden = !many;
          // Arrows should feel instant: warm both neighbours' full-size copies
          // while the shopper looks at this one, so the slide-in usually lands
          // already sharp instead of thumbnail-first.
          if (many) {
            [1, -1].forEach((d) => {
              const nb = list[(i + d + list.length) % list.length];
              if (nb) { const w = new Image(); w.src = hiRes(nb.url); }
            });
          }
        };

        /* ---- Arrow-press slide, matching the main gallery's motion ----
           The incoming photo rides the real frame in from one side while a
           GHOST — a background-image snapshot of whatever was on screen — rides
           out the other. background contain/centre has identical geometry to
           the object-fit img, so the ghost is pixel-identical to what the
           shopper was just looking at, whether that was the decoded full-size
           copy or still the thumbnail placeholder. */
        let slide = null; // { ghost, onEnd, timer } while a slide is in flight

        /* Finish any in-flight slide NOW. Idempotent; called by the next step
           (rapid presses become a fast sequence of settled steps, not a
           pile-up), by teardown, by the transition's own end, and by the timer
           backstop — transitions never advance in a hidden document, so a
           timer must always be able to land the state. */
        const settleSlide = () => {
          if (!slide) return;
          clearTimeout(slide.timer);
          frame.removeEventListener('transitionend', slide.onEnd);
          if (slide.ghost.parentNode) slide.ghost.parentNode.removeChild(slide.ghost);
          frame.classList.remove('is-anim');
          frame.style.transform = '';
          slide = null;
        };

        const step = (d) => {
          if (!list.length) return;
          const from = i;
          i = (i + d + list.length) % list.length;
          if (i === from) { paint(); return; }
          // Reduced motion swaps instantly, same as the theme's other motion.
          if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { paint(); return; }
          settleSlide();
          const ghost = document.createElement('div');
          /* ⚠ `no-empty` is load-bearing. theme.css:1185 hides every childless
             div — `div:empty:not(…, .no-empty)` — and this one is painted
             purely by background-image, so it has no children. Without the
             class it was display:none for its whole life: the outgoing photo
             did not slide out, it simply vanished, exposing the figure behind
             it for the length of the animation. That was Owen's "flash". */
          ghost.className = 'wb-lb__frame wb-lb__ghost is-anim no-empty';
          ghost.setAttribute('aria-hidden', 'true');
          const showing = (img.classList.contains('is-loaded') && img.currentSrc) ||
            (list[from] && list[from].url) || '';
          /* The ghost's sharp snapshot rides ::after (--wb-lb-snap), NOT the
             element background — that paints UNDER pseudo-elements, so the blur
             would cover it. Its blurred backdrop is the OUTGOING frame's,
             captured before paint() overwrites the var with the new photo's. */
          if (showing) ghost.style.setProperty('--wb-lb-snap', 'url("' + encodeURI(showing).replace(/"/g, '%22') + '")');
          const outgoingBg = frame.style.getPropertyValue('--wb-lb-bg');
          if (outgoingBg) ghost.style.setProperty('--wb-lb-bg', outgoingBg);
          fig.insertBefore(ghost, frame);
          paint(); // the real frame now holds the NEW photo
          /* ⚠ Start state committed via forced reflow, never rAF — a throttled
             renderer leaves an rAF-parked start state unapplied indefinitely
             (the wb-drawer.js lesson). */
          frame.style.transform = 'translateX(' + (d > 0 ? 100 : -100) + '%)';
          void frame.offsetWidth;
          frame.classList.add('is-anim');
          frame.style.transform = 'translateX(0)';
          ghost.style.transform = 'translateX(' + (d > 0 ? -100 : 100) + '%)';
          /* ⚠ transitionend BUBBLES — the img's own load-fade fires it from
             inside the frame — so filter on target, exactly like wb-drawer.js. */
          const onEnd = (e) => { if (e.target === frame) settleSlide(); };
          frame.addEventListener('transitionend', onEnd);
          slide = { ghost: ghost, onEnd: onEnd, timer: setTimeout(settleSlide, 380) };
        };

        /* Bound once, not per paint. On error we reveal it anyway: a photo that
           genuinely will not load should show its alt text rather than leave the
           frame silently showing the previous shopper's placeholder. */
        img.addEventListener('load', () => img.classList.add('is-loaded'));
        img.addEventListener('error', () => img.classList.add('is-loaded'));

        /* ⚠ Teardown does NOT hang off the dialog's `close` event. Chromium has
           been observed here not firing it at all (verified 2026-08-16 against a
           native, unpatched close()), and wb-drawer.js defers the real close()
           behind transitionend anyway — so anything bound to the ✕ click would
           run mid-animation. wb-drawer.js dispatches `wb:closed` after the
           dialog has actually shut, which is the reliable hook. The native event
           is kept as a second entry point; teardown is idempotent. */
        const teardown = () => {
          settleSlide();
          phEl.style.backgroundImage = '';
          frame.style.removeProperty('--wb-lb-bg');
          img.removeAttribute('src');
          /* ⚠ Restore focus ourselves. The contract does not track what opened a
             dialog, and the platform's own restoration anchors on whatever was
             focused at showModal() time — which on Safari is <body>, because it
             does not focus a <button> on click. Without this the shopper is
             dropped at the top of the document. */
          if (opener && document.contains(opener)) {
            try { opener.focus({ preventScroll: true }); } catch (e) { opener.focus(); }
          }
          opener = null;
        };

        prev.addEventListener('click', () => step(-1));
        next.addEventListener('click', () => step(1));
        el.addEventListener('keydown', (e) => {
          if (e.metaKey || e.ctrlKey || e.altKey) return;
          if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
          if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
        });
        /* The ✕, the backdrop and ESC are all wb-drawer.js's job now — it owns
           [data-wb-drawer-close], the dialog-element-only backdrop test, and the
           `cancel` interception that lets ESC animate out. */
        el.addEventListener('wb:closed', teardown);
        el.addEventListener('close', teardown);

        return {
          setPhotos(next) { list = next; },
          /* ⚠ PAINT ONLY — it must never call showModal(). wb-drawer.js opens
             the dialog from the tile's data-wb-drawer attribute; opening it here
             as well would show it WITHOUT .is-in, which is opacity:0 — a fully
             invisible dialog holding the page inert. Returns whether the photo
             resolved, so the caller can veto the open. */
          show(index, fromEl) {
            if (!list.length || !(index >= 0) || index >= list.length) return false;
            i = index;
            opener = fromEl || null;
            paint();
            return true;
          },
          indexOfUrl(u) {
            const key = photoKey(u);
            return list.findIndex((ph) => photoKey(ph.url) === key);
          }
        };
      })();

      /* ⚠ Bound on the CONTAINERS, not on document — and that is load-bearing.
         wb-drawer.js opens the dialog from a delegated listener on document; a
         listener on an element BETWEEN the tile and document sees the click
         first as it bubbles, so the right photo is always painted before the
         dialog appears. On document we would be racing registration order
         (wb-drawer.js is a layout-head defer, this file a section-level one)
         and would lose, flashing the previous shopper's photo on every open.

         ⚠ It also preserves the VETO. wb-drawer.js opens on the mere presence of
         the attribute, so without this an unresolvable photo would open an empty
         dialog and trap a screen-reader user in it. stopPropagation() is how the
         open is called off — the tile stays inert, exactly as before.

         Both containers are server-rendered and only ever have their innerHTML
         replaced, so these two listeners survive every sort, filter and fetched
         page without re-binding. */
      const armPhotos = (root) => {
        if (!root) return;
        root.addEventListener('click', (e) => {
          const tile = e.target.closest && e.target.closest('[data-wb-lb-i], [data-wb-lb-url]');
          if (!tile) return;
          let ok = false;
          try {
            const byIndex = tile.getAttribute('data-wb-lb-i');
            const idx = byIndex !== null
              ? parseInt(byIndex, 10)
              : lb.indexOfUrl(tile.getAttribute('data-wb-lb-url'));
            ok = lb.show(idx, tile);
          } catch (err) {
            ok = false;   // a paint that threw must not leave a stale photo showing
          }
          if (!ok) { e.preventDefault(); e.stopPropagation(); }
        });
      };

      /* Photo deck. Dedup by PATHNAME via photoKey — the same file arrives with
         different query strings AND from two different Judge.me hosts. */
      const deck = document.querySelector('[data-wb-photos]');
      const strip = document.querySelector('[data-wb-strip]');
      // The two surfaces that hold photo tiles. Bound once — both are
      // server-rendered and only ever have their innerHTML replaced.
      armPhotos(strip);
      armPhotos(out);

      const paintPhotos = () => {
        if (!deck || !strip) return;
        const seen = new Set();
        const photos = [];
        /* ⚠ Carry the REVIEW with each photo. The dedupe used to flatten to bare
           URLs, which threw away the one thing the viewer needs — whose review a
           photo belongs to. Reviews are added first so that a photo appearing in
           BOTH a review and the gallery feed keeps its review; the gallery copy
           is then skipped as a duplicate. */
        const add = (u, review) => {
          if (!u) return;
          const key = photoKey(u);
          if (seen.has(key)) return;
          seen.add(key);
          photos.push({ url: u, review: review || null });
        };
        reviews.forEach((r) => r.pics.forEach((u) => add(u, r)));
        const galleryEl = panel.querySelector('.jdgm-gallery-data');
        if (galleryEl) {
          try {
            JSON.parse(galleryEl.getAttribute('data-json') || '[]').forEach((g) => {
              // No review behind these: the viewer drops its text panel for them.
              (g.pictures_urls || []).forEach((u) => add(u.compact || u.original, null));
            });
          } catch (e) { /* malformed payload: fall back to the rendered pics */ }
        }
        /* ⚠ No early return on an empty set. Bailing before the two lines below
           meant a deck shown once could never hide again, and the viewer kept
           browsing a list the page had already replaced — this runs after EVERY
           fetched page, not just the first. Hide/show both directions instead. */
        lb.setPhotos(photos);
        if (!photos.length) { deck.hidden = true; return; }
        /* Buttons, not spans: this is a control that opens a dialog, so it has to
           be reachable and operable from the keyboard. data-wb-lb-i is the index
           into the shared photo list, which is what makes the deck and the
           in-review thumbnails browse ONE gallery (Owen, 2026-08-16). */
        strip.innerHTML = photos.map((ph, i) =>
          '<button type="button" class="wb-rv__tile" data-wb-lb-i="' + i + '"' +
          // The trigger the shared contract opens from; omitted when there is no
          // usable dialog, leaving an inert tile rather than a dead control.
          (LB_ID ? ' data-wb-drawer="' + esc(LB_ID) + '" aria-haspopup="dialog"' : '') +
          ' aria-label="' + esc(photoLabel(ph, i, photos.length)) + '">' +
          '<img src="' + esc(ph.url) + '" alt="" loading="lazy" decoding="async"></button>').join('');
        deck.hidden = false;
      };

      /* "N of M are verified purchases" only ever renders from the full
         set — hidden rather than briefly wrong. */
      const vLine = document.querySelector('[data-wb-verified]');
      const paintVerified = () => {
        if (!vLine || !vLine.dataset.tmpl) return;
        if (reviews.length < total) { vLine.hidden = true; return; }
        const n = reviews.filter((r) => r.verified).length;
        vLine.textContent = vLine.dataset.tmpl.replace('@@N@@', n);
        vLine.hidden = n === 0;
      };

      const repaintData = () => { renderAll(); applyView(); paintPhotos(); paintVerified(); paintToggles(); };

      /* Resolves {ok, count} — NEVER rejects, and every terminal state is
         distinguishable: {ok:false} is a network/HTTP failure, {ok:true,
         count:0} is the API's genuine end-of-list. Callers stop on either. */
      const fetchPage = (n, replace) => {
        if (!productId || !shop || typeof fetch !== 'function') return Promise.resolve({ ok: false, count: 0 });
        const q = new URLSearchParams({
          url: shop, shop_domain: shop, platform: 'shopify',
          page: String(n), per_page: String(PER), product_id: productId
        });
        return fetch(API + '?' + q, { credentials: 'omit' })
          .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
          .then((j) => {
            // The API's count corrects a stale rating_count metafield even
            // on an empty page — otherwise "8 expected, 5 exist" chases
            // three reviews that are never coming.
            if (typeof j.total_count === 'number') total = j.total_count;
            const got = parse(new DOMParser().parseFromString(j.html || '', 'text/html').body);
            if (!got.length) { applyView(); paintVerified(); paintToggles(); return { ok: true, count: 0 }; }
            // Page 1 replaces the seed (it is a superset); later pages
            // extend. De-duplicate by review id either way.
            const seen = new Set(replace ? [] : reviews.map((r) => r.id));
            const base = replace ? [] : reviews;
            reviews = base.concat(got.filter((r) => !r.id || !seen.has(r.id)));
            page = n;
            repaintData();
            return { ok: true, count: got.length };
          })
          // A failed fetch must never empty the list: the seed stays.
          .catch(() => ({ ok: false, count: 0 }));
      };

      repaintData();

      /* Page-1 state tracked explicitly: `page` starting at 1 only means
         "the seed came from page 1" — the seed is 5 records of a page that
         may hold 50. */
      let page1Done = reviews.length >= total;
      let eagerPending = Promise.resolve();
      if (reviews.length < total) {
        eagerPending = fetchPage(1, true).then((r) => { if (r.ok) page1Done = true; });
      }

      /* Fetch everything still missing. Serialised behind the eager fetch
         so a late page-1 replace can never clobber appended pages; attempts
         are bounded by requests made, so a failing endpoint cannot spin. */
      const fillAll = () => {
        let attempts = 0;
        const step = () => {
          if (reviews.length >= total || attempts >= 12) return Promise.resolve();
          attempts += 1;
          return fetchPage(page + 1, false).then((r) =>
            (r.ok && r.count > 0 ? step() : undefined));
        };
        return eagerPending
          .then(() => {
            if (page1Done) return step();
            attempts += 1;
            return fetchPage(1, true).then((r) => {
              if (r.ok) { page1Done = true; return step(); }
              // Page 1 unrecoverable right now: STOP with the seed. Marching
              // on to page 2 would present reviews 51+ appended straight
              // after review 5 — a gapped list dressed as a complete one.
              return undefined;
            });
          });
      };

      if (more) more.addEventListener('click', () => {
        more.disabled = true;
        // Filters stay clickable while the fill runs; if one reset the cap
        // mid-flight, honouring the stale +5 would jump the fresh view to
        // ten rows. The version stamp keeps the increment scoped to the
        // view the visitor actually pressed the button on.
        const v = viewVersion;
        fillAll().then(() => {
          if (viewVersion === v) shown += PAGE;
          more.disabled = false;
          applyView();
        });
      });

      const resetCap = () => { shown = PAGE; viewVersion += 1; };
      const clearAll = () => { starFilter = null; onlyVerified = false; onlyPhotos = false; resetCap(); applyView(); };

      /* The histogram IS the star filter. Bars sit well above the list on
         phones, so a tap scrolls to the tab row — otherwise it produces no
         visible response and the natural second tap silently clears. */
      bars.forEach((btn) => {
        btn.addEventListener('click', () => {
          const n = parseInt(btn.dataset.wbFilter, 10);
          starFilter = starFilter === n ? null : n;
          resetCap();
          applyView();
          // The bar's count is product-wide (server-side) but the filter
          // runs over memory: on a >50-review product a bucket can live
          // entirely on an unfetched page, so complete the set or the bar
          // says "2" over an empty list.
          ensureFull();
          if (window.matchMedia('(max-width: 749px)').matches && tabrow) {
            tabrow.scrollIntoView({ block: 'start', behavior: smooth() });
          }
        });
      });
      if (vToggle) vToggle.addEventListener('click', () => { onlyVerified = !onlyVerified; resetCap(); applyView(); ensureFull(); });
      if (pToggle) pToggle.addEventListener('click', () => { onlyPhotos = !onlyPhotos; resetCap(); applyView(); ensureFull(); });
      if (clear) clear.addEventListener('click', clearAll);
      if (clearAllBtn) clearAllBtn.addEventListener('click', clearAll);
      if (sortSel) sortSel.addEventListener('change', () => {
        sortMode = sortSel.value;
        resetCap();
        renderAll();
        applyView();
        ensureFull();
      });
      /* A filtered or re-sorted view over a partial set would quietly lie on
         >50-review products; the first use of any list control completes the
         set in the background. */
      let ensured = false;
      const ensureFull = () => {
        if (ensured || reviews.length >= total) { ensured = true; return; }
        ensured = true;
        fillAll();
      };
    } else {
      // The server paints "Show N more reviews" optimistically from the
      // rating_count metafield; with no parseable widget seed there is no
      // renderer behind it, so an orphaned button must not sit there dead.
      const orphanMore = document.querySelector('[data-wb-more]');
      if (orphanMore) orphanMore.hidden = true;
    }

    /* =================== Tabs (Reviews | Questions) =================== */

    // Set by the questions module: work that must wait until the Questions
    // pane is actually visible (the answer clamp measures scrollHeight,
    // which is 0 for anything inside the [hidden] pane).
    let onQPaneShown = null;

    const tabR = document.querySelector('[data-wb-rvtab="r"]');
    const tabQ = document.querySelector('[data-wb-rvtab="q"]');
    const paneR = document.querySelector('[data-wb-pane="r"]');
    const paneQ = document.querySelector('[data-wb-pane="q"]');
    const tabline = document.querySelector('[data-wb-tabline]');
    const controls = document.querySelector('[data-wb-controls]');

    const moveTabline = (btn) => {
      if (!tabline || !btn) return;
      // First paint positions instantly then fades in — otherwise the
      // indicator flies in from x=0.
      const instant = tabline.style.opacity !== '1';
      if (instant) tabline.style.transition = 'none';
      tabline.style.left = btn.offsetLeft + 'px';
      tabline.style.width = btn.offsetWidth + 'px';
      if (instant) { void tabline.offsetWidth; tabline.style.transition = ''; }
      tabline.style.opacity = '1';
    };

    const selectTab = (which) => {
      if (!tabR || !tabQ) return;
      const isQ = which === 'q';
      tabR.setAttribute('aria-selected', isQ ? 'false' : 'true');
      tabQ.setAttribute('aria-selected', isQ ? 'true' : 'false');
      tabR.tabIndex = isQ ? -1 : 0;
      tabQ.tabIndex = isQ ? 0 : -1;
      moveTabline(isQ ? tabQ : tabR);
      if (paneR) paneR.hidden = isQ;
      if (paneQ) paneQ.hidden = !isQ;
      // Sort + filters apply to reviews, not questions.
      if (controls) controls.hidden = isQ;
      // The ask form lives in its own modal drawer now, so switching tabs
      // never touches it — a half-typed question survives.
      if (isQ && typeof onQPaneShown === 'function') onQPaneShown();
    };

    if (tabR && tabQ) {
      tabR.addEventListener('click', () => selectTab('r'));
      tabQ.addEventListener('click', () => selectTab('q'));
      [tabR, tabQ].forEach((t) => {
        t.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const next = t === tabR ? tabQ : tabR;
            selectTab(next === tabQ ? 'q' : 'r');
            next.focus();
          }
          if (e.key === 'Home') { e.preventDefault(); selectTab('r'); tabR.focus(); }
          if (e.key === 'End') { e.preventDefault(); selectTab('q'); tabQ.focus(); }
        });
      });
      moveTabline(tabR);
      /* The snapshot goes stale twice: Barlow swaps in after DOMContentLoaded
         (font-display: swap re-metrics the uppercase labels) and the 749px
         breakpoint changes tab font-size and gap. Re-measure the SELECTED
         tab on both. */
      const remeasure = () => moveTabline(tabQ.getAttribute('aria-selected') === 'true' ? tabQ : tabR);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(remeasure);
      let remeasureT = null;
      window.addEventListener('resize', () => {
        clearTimeout(remeasureT);
        remeasureT = setTimeout(remeasure, 150);
      });
    }

    /* =================== Questions =================== */

    const qlist = document.querySelector('[data-wb-qlist]');
    const qfail = document.querySelector('[data-wb-qfail]');

    if (qlist && panel) {
      const BADGE = (qlist.dataset.shopBadge || '').trim();

      const parseQuestions = () => [...panel.querySelectorAll('.jdgm-quest')].map((q) => ({
        id: q.getAttribute('data-question-id') || '',
        asker: txt(q, '.jdgm-quest__asker .jdgm-rev__author') || 'Anonymous',
        date: (() => { const t = q.querySelector('.jdgm-rev__header .jdgm-rev__timestamp'); return t ? (t.getAttribute('data-content') || '').split(' ')[0] : ''; })(),
        body: bodyText(q.querySelector('.jdgm-quest__body')),
        answers: [...q.querySelectorAll('.jdgm-quest__answers .jdgm-ans')].map((a) => ({
          /* ⚠ data-from-shop is NOT reliable: on this store 14 of 15
             merchant-written answers ship it as "false" (verified
             2026-08-09), which labelled the store's own replies
             "PILOT GEAR · CUSTOMER". The replier name is the trustworthy
             signal, so either one identifies the store speaking. */
          fromShop: a.getAttribute('data-from-shop') === 'true'
            || (txt(a, '.jdgm-rev__replier') || '').trim().toUpperCase() === BADGE,
          replier: txt(a, '.jdgm-rev__replier'),
          date: (() => { const t = a.querySelector('.jdgm-rev__timestamp'); return t ? (t.getAttribute('data-content') || '').split(' ')[0] : ''; })(),
          body: bodyText(a.querySelector('.jdgm-ans__body'))
        }))
      }));

      const ansHTML = (a) => {
        const who = a.fromShop
          ? '<span class="wb-rv__ansbadge">' + esc(BADGE) + '</span> ' + esc(String(I18N.replied || '').replace('@@D@@', fmtDate(a.date)))
          : esc(a.replier || 'Anonymous') + ' &middot; ' + esc(I18N.customer || 'Customer') + ' &middot; ' + esc(fmtDate(a.date));
        return (
          '<div class="wb-rv__ans' + (a.fromShop ? '' : ' wb-rv__ans--community') + '">' +
            '<div class="wb-rv__answho">' + who + '</div>' +
            '<div class="wb-rv__ansclip"><p class="wb-rv__anstext">' + esc(a.body) + '</p></div>' +
          '</div>'
        );
      };

      const renderQuestions = (qs) => {
        qlist.innerHTML = qs.map((q) => (
          '<article class="wb-rec-row wb-rec-row--q">' +
            '<div class="wb-rec-row__who">' +
              '<div class="wb-rec-row__name">' + esc(q.asker) + '</div>' +
              '<div class="wb-rec-row__date">' + esc(fmtDate(q.date)) + '</div>' +
            '</div>' +
            '<div class="wb-rec-row__body">' +
              '<div class="wb-rv__qtext">' + esc(q.body) + '</div>' +
              (q.answers.length ? '<div class="wb-rv__answers">' + q.answers.map(ansHTML).join('') + '</div>' : '') +
            '</div>' +
          '</article>'
        )).join('');

        applyAnswerClamps();
      };

      /* Clamp long ANSWERS only (questions never clamp), and only on real
         overflow past 14 lines — a tight clamp hides exactly the technical
         detail the section exists to surface.
         ⚠ scrollHeight is 0 for anything inside the [hidden] Questions
         pane, and on every product with reviews that pane IS hidden while
         the questions load in the background — measuring then would
         silently disable the clamp forever. So measurement waits for the
         pane to be visible: immediately on questions-only products, on
         first tab switch otherwise (selectTab calls back via onQPaneShown). */
      let clampsDone = false;
      const applyAnswerClamps = () => {
        if (clampsDone || !qlist.offsetParent) return;
        clampsDone = true;
        qlist.querySelectorAll('.wb-rv__ans').forEach((ans) => {
          const t = ans.querySelector('.wb-rv__anstext');
          const lh = parseFloat(getComputedStyle(t).lineHeight) || 26;
          if (t.scrollHeight > lh * 14) {
            ans.classList.add('is-clamped');
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'wb-rv__ansmore';
            b.textContent = I18N.show_full || 'Show full answer';
            b.addEventListener('click', () => {
              const on = ans.classList.toggle('is-clamped');
              b.textContent = on ? (I18N.show_full || 'Show full answer') : (I18N.show_less || 'Show less');
            });
            ans.appendChild(b);
          }
        });
      };
      onQPaneShown = applyAnswerClamps;

      /* Their bundle builds question rows only after its Questions subtab
         has been activated once, and pages them 5 at a time — both happen
         invisibly inside the hidden panel, then the parsed set renders in
         the design's language. */
      let qLoaded = false;
      const loadQuestions = () => {
        if (qLoaded) return;
        const start = Date.now();
        const mountWait = setInterval(() => {
          if (!widgetMounted()) {
            if (Date.now() - start > 20000) {
              clearInterval(mountWait);
              if (qfail) qfail.hidden = false;
            }
            return;
          }
          clearInterval(mountWait);
          // Activate their subtab (label is localised — use their setting).
          const spans = [...panel.querySelectorAll('.jdgm-subtab__name')];
          const label = (window.jdgmSettings && window.jdgmSettings.widget_questions_subtab_text) || 'Questions';
          const qTab = spans.find((s) => (s.textContent || '').indexOf(label) !== -1)
            || (spans.length > 1 ? spans.find((s) => !s.classList.contains('jdgm--active')) : null);
          if (qTab) qTab.click();
          let iter = 0;
          let lastCount = -1;
          let stable = 0;
          const pager = setInterval(() => {
            iter += 1;
            const count = panel.querySelectorAll('.jdgm-quest').length;
            const loadMoreBtn = panel.querySelector('.jdgm-quest-widg__body .jdgm-paginate__load-more');
            /* ⚠ NOT offsetParent: inside the hidden panel it is null for
               everything, which read as "no more pages" at 5 of 10. The
               widget retires an exhausted load-more via its own display,
               and an element's OWN computed display is readable even under
               a display:none ancestor. The stable-count guard covers a
               button that neither works nor retires. */
            const morePages = loadMoreBtn && getComputedStyle(loadMoreBtn).display !== 'none';
            stable = count === lastCount ? stable + 1 : 0;
            lastCount = count;
            if (count > 0 && (!morePages || stable >= 3)) {
              clearInterval(pager);
              qLoaded = true;
              renderQuestions(parseQuestions());
              if (qfail) qfail.hidden = true;
              return;
            }
            if (morePages) loadMoreBtn.click();
            if (iter >= 25) {
              clearInterval(pager);
              const qs = parseQuestions();
              if (qs.length) { qLoaded = true; renderQuestions(qs); }
              else if (qfail) qfail.hidden = false;
            }
          }, 700);
        }, 500);
      };
      loadQuestions();
      if (qfail) qfail.querySelector('[data-wb-qretry]')?.addEventListener('click', () => {
        qfail.hidden = true;
        loadQuestions();
      });
    }

    /* =================== ASK A QUESTION =================== */

    const ask = document.querySelector('[data-wb-ask]');
    if (ask && panel && typeof panel.showModal === 'function') {
      /* A modal drawer never scrolls the page — that is the whole point of
         it. Nothing in this flow calls scrollIntoView.

         ⚠ Open and close are wb-drawer.js's, called through WBDrawer rather
         than reimplemented here. This drawer carries .wb-drawer, so that file's
         ESC and backdrop handlers were ALWAYS going to fire for it — and while
         this block hand-rolled its own matching pair, every dismissal ran two
         close routines with two transitionend listeners and two 400ms timers
         racing (verified 2026-08-17: close() called twice on a single ESC).
         Meanwhile showModal() by hand skipped the OPEN half entirely, so the
         iOS 26 chrome tint never applied to this drawer — tintOff ran on every
         close against a count tintOn had never incremented.
         The local copy also listened for any bubbling transitionend, which
         Judge.me's own form could satisfy mid-slide; the shared one filters. */
      const wbd = () => window.WBDrawer;
      const closeDrawer = () => { if (wbd()) wbd().close(panel); else panel.close(); };

      const openDrawer = () => {
        if (wbd()) wbd().open(panel);
        else { panel.showModal(); void panel.offsetWidth; panel.classList.add('is-in'); }
        /* Retried, exactly like the write-review relay: their bundle binds
           the opener seconds after mounting it, so one click can land on a
           dead element. The height guard makes every retry safe — their
           opener TOGGLES, and re-clicking an open form would fold it shut. */
        let tries = 0;
        const poke = setInterval(() => {
          tries += 1;
          const form = panel.querySelector('.jdgm-question-form');
          if (form && form.offsetHeight > 0) { clearInterval(poke); return; }
          const opener = panel.querySelector('.jdgm-ask-question-btn');
          if (opener) opener.click();
          if (tries >= 8) clearInterval(poke);
        }, 500);
      };

      ask.addEventListener('click', (e) => {
        /* ⚠ The href (contact page) is the working fallback whenever
           Judge.me's bundle is content-blocked — preventDefault would turn
           this click into a dead end. */
        if (!widgetMounted() && !loaderAlive()) return;
        e.preventDefault();
        if (widgetMounted()) { openDrawer(); return; }
        // Loader ran but the bundle is still mounting: open the drawer now
        // (its own retry loop catches the form the moment it exists) and
        // concede to the contact page only if it never arrives.
        openDrawer();
        let tries = 0;
        const poll = setInterval(() => {
          tries += 1;
          if (widgetMounted()) { clearInterval(poll); return; }
          if (tries >= 10) {
            clearInterval(poll);
            /* ⚠ Through the contract, not panel.close(). Opening tinted the
               page navy for iOS; closing past the contract would leave
               wb-top-layer-open stuck on <html> and the body navy behind the
               contact page we are about to navigate to. */
            closeDrawer();
            window.location.assign(ask.getAttribute('href'));
          }
        }, 500);
      });

      /* The ✕ (data-wb-drawer-close), ESC (via `cancel`) and the backdrop are
         all wb-drawer.js's — it was already handling the last two for this
         dialog regardless. Re-binding them here is what made every dismissal
         run twice. */
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
